import React, { useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  ExternalLink,
  FileText,
  FlaskConical,
  ImageIcon,
  Inbox,
  Layers3,
  Pencil,
  RadioTower,
  RefreshCcw,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { CommandHero } from "@/components/shell/CommandHero";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { PlatformBadge } from "@/components/badges/PlatformBadge";
import { PlatformPreviewPanel } from "@/components/content/PlatformPreviewPanel";
import { ApprovalActions } from "@/components/content/ApprovalActions";
import { useAppState } from "@/state/AppStateProvider";
import { buildProjectHub } from "@/lib/projectWorkspace";
import { ASSET_TYPE_LABELS } from "@/lib/constants";
import { getRunCoverPrompt } from "@/lib/imagePromptAssets";
import { shouldAttachCoverToAsset } from "@/lib/mediaAspect";
import { cn, formatDateTime, timeAgo } from "@/lib/utils";

export default function ProjectDetail() {
  const { id } = useParams();
  const { state, updateAsset, approveAsset, rejectAsset, reviseAsset, generateImage, uploadImage } = useAppState();
  const project = useMemo(() => state.projects.find((item) => item.id === id), [state.projects, id]);
  const hub = useMemo(() => buildProjectHub({
    project,
    assets: state.assets,
    calendarItems: state.calendar,
    inbox: state.inbox,
    runs: state.runs,
  }), [project, state.assets, state.calendar, state.inbox, state.runs]);

  const [supportingOpen, setSupportingOpen] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const coverUploadRef = useRef(null);

  if (!hub) {
    return (
      <div data-testid="project-not-found" className="space-y-4">
        <Button asChild variant="ghost" size="sm" className="gap-1.5">
          <Link to="/projects"><ArrowLeft className="h-4 w-4" /> Back to projects</Link>
        </Button>
        <div className="rounded-xl border bg-card p-10 text-center inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
          <p className="text-sm font-semibold text-1">Project not found.</p>
          <p className="mt-1 text-xs text-muted-foreground">It may have been archived or removed.</p>
        </div>
      </div>
    );
  }

  const sharedCover = hub.supporting.find((asset) => asset.asset_type === "image_prompt")?.cover_image
    || hub.deliverables.find((asset) => asset.cover_image)?.cover_image
    || hub.cover_image
    || null;
  const coverTargets = hub.assets.filter((asset) => asset.asset_type === "image_prompt" || shouldAttachCoverToAsset(asset));

  const replaceSharedCover = (coverImage) => {
    coverTargets.forEach((asset) => updateAsset(asset.id, { cover_image: coverImage }));
  };

  const onRegenerateCover = async () => {
    setCoverBusy(true);
    const pending = toast.loading("Generating cover…");
    try {
      const prompt = getRunCoverPrompt(hub.assets)
        || `A polished, brand-safe social media cover image for "${hub.title}". No text, logos, or watermarks.`;
      const res = await generateImage(prompt, undefined, "wide");
      if (res?.ok) { replaceSharedCover(res.url); toast.success("Cover regenerated", { id: pending }); }
      else toast.error(`Image generation failed: ${res?.message || "unknown error"}`, { id: pending });
    } finally {
      setCoverBusy(false);
    }
  };

  const onUploadCover = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Choose an image file");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image too large - keep it under 12MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      setCoverBusy(true);
      const pending = toast.loading("Uploading cover…");
      try {
        const res = await uploadImage(reader.result);
        if (res?.ok) { replaceSharedCover(res.url); toast.success(`${file.name} applied`, { id: pending }); }
        else toast.error(`Upload failed: ${res?.message || "unknown error"}`, { id: pending });
      } finally {
        setCoverBusy(false);
      }
    };
    reader.onerror = () => toast.error("Could not load image");
    reader.readAsDataURL(file);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 pb-20" data-testid="project-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-1" data-testid="project-back">
          <Link to="/projects"><ArrowLeft className="h-4 w-4" /> Projects</Link>
        </Button>
        <Button asChild className="gap-1.5" data-testid="project-new-run" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
          <Link to="/create"><Sparkles className="h-4 w-4" /> Create from project</Link>
        </Button>
      </div>

      <CommandHero
        dark
        eyebrow={hub.pillars?.join(" · ") || "Project"}
        title={hub.title}
        description={hub.description}
        testId="project-detail-hero"
        visual={<ProjectHeroVisual hub={hub} />}
      />

      <section className="grid gap-3 md:grid-cols-4" data-testid="project-command-strip">
        <HubMetric icon={Layers3} label="Assets" value={hub.metrics.assets} hint={`${hub.metrics.deliverables} deliverables`} />
        <HubMetric icon={ClipboardCheck} label="Needs review" value={hub.metrics.needsReview} hint="Human decisions" />
        <HubMetric icon={CalendarClock} label="Scheduled" value={hub.metrics.scheduled} hint="Calendar handoffs" />
        <HubMetric icon={Inbox} label="Source material" value={hub.metrics.relatedInbox} hint="Related inbox items" />
      </section>

      <section className="grid gap-6 xl:grid-cols-12">
        <div className="min-w-0 space-y-6 xl:col-span-8">
          <DeliverablesSection
            deliverables={hub.deliverables}
            approveAsset={approveAsset}
            rejectAsset={rejectAsset}
            reviseAsset={reviseAsset}
          />

          <SupportingWorkSection
            supporting={hub.supporting}
            sharedCover={sharedCover}
            open={supportingOpen}
            setOpen={setSupportingOpen}
            onRegenerateCover={onRegenerateCover}
            onUploadCover={() => coverUploadRef.current?.click()}
            coverBusy={coverBusy}
          />
          <input ref={coverUploadRef} type="file" accept="image/*" className="hidden" onChange={onUploadCover} data-testid="project-cover-input" />
        </div>

        <aside className="min-w-0 space-y-4 xl:col-span-4 xl:sticky xl:top-20 xl:self-start">
          <NextActionPanel hub={hub} />
          <CalendarPanel items={hub.calendarItems} />
          <SourceMaterialPanel items={hub.relatedInbox} />
          <RunsPanel runs={hub.runs} />
        </aside>
      </section>
    </motion.div>
  );
}

function ProjectHeroVisual({ hub }) {
  return (
    <div className="space-y-3" data-testid="project-hero-summary">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-foreground">Campaign progress</p>
          <p className="text-[12px] text-muted-foreground">{hub.progressPercent}% complete</p>
        </div>
        <StatusBadge status={hub.status} />
      </div>
      <Progress value={hub.progressPercent} className="h-2" />
      <div className="grid grid-cols-2 gap-2">
        <MiniStat icon={Layers3} label="Deliverables" value={hub.metrics.deliverables} />
        <MiniStat icon={RadioTower} label="Runs" value={hub.metrics.runs} />
        <MiniStat icon={CalendarClock} label="Next post" value={hub.nextCalendarItem ? formatDateTime(hub.nextCalendarItem.scheduled_at).split(" · ")[0] : "None"} />
        <MiniStat icon={Inbox} label="Sources" value={hub.metrics.relatedInbox} />
      </div>
    </div>
  );
}

function DeliverablesSection({ deliverables, approveAsset, rejectAsset, reviseAsset }) {
  return (
    <section className="space-y-3" data-testid="project-deliverables">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-1">Deliverables</h2>
          <p className="text-[12px] text-muted-foreground">Platform-ready content that can be edited, approved, and scheduled.</p>
        </div>
        <span className="text-[11.5px] text-muted-foreground">{deliverables.length} deliverable{deliverables.length === 1 ? "" : "s"}</span>
      </div>
      {deliverables.length === 0 ? (
        <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
          No deliverables yet. Start a run from this project when you are ready.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {deliverables.map((asset) => (
            <article key={asset.id} className="space-y-2" data-testid={`project-deliverable-${asset.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <PlatformBadge platform={asset.platform} compact />
                  <span className="line-clamp-1 text-[11.5px] text-muted-foreground">{asset.title}</span>
                </div>
                <StatusBadge status={asset.status} />
              </div>
              <PlatformPreviewPanel asset={asset} compact className="shadow-none" bodyClassName="bg-[color:var(--surface-2)]" mobilePreviewContext="review" />
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border p-2.5" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}>
                <ApprovalActions
                  onApprove={() => { approveAsset(asset.id); toast.success("Approved"); }}
                  onRevise={() => { reviseAsset(asset.id); toast.message("Sent back for revision"); }}
                  showRegenerate={false}
                  onReject={() => { rejectAsset(asset.id); toast.error("Rejected"); }}
                />
                <Button asChild size="sm" variant="outline" className="gap-1.5" data-testid={`project-edit-${asset.id}`}>
                  <Link to={`/library/${asset.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</Link>
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function SupportingWorkSection({ supporting, sharedCover, open, setOpen, onRegenerateCover, onUploadCover, coverBusy }) {
  if (supporting.length === 0 && !sharedCover) return null;

  return (
    <section className="rounded-xl border bg-card inner-highlight" style={{ borderColor: "var(--border-subtle)" }} data-testid="project-supporting">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between gap-2 px-5 py-3.5 text-left hover:bg-[color:var(--surface-1)]"
        data-testid="project-supporting-toggle"
      >
        <div className="flex items-center gap-2.5">
          {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <FlaskConical className="h-4 w-4 text-[color:var(--accent-cyan)]" />
          <div>
            <p className="text-sm font-semibold text-1">Agent work and visual assets</p>
            <p className="text-[11.5px] text-muted-foreground">Research notes and cover prompts support the campaign, but do not ship directly.</p>
          </div>
        </div>
        <span className="text-[11px] text-muted-foreground">{supporting.length} item{supporting.length === 1 ? "" : "s"}</span>
      </button>
      {open && (
        <div className="border-t p-5" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {sharedCover && (
              <div className="rounded-xl border bg-[color:var(--surface-1)] p-4" style={{ borderColor: "var(--border-subtle)" }} data-testid="project-shared-cover">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="h-3.5 w-3.5 text-[color:var(--accent-cyan)]" />
                    <h4 className="text-[13px] font-semibold text-1">Shared cover image</h4>
                  </div>
                  <span className="text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground">Visual system</span>
                </div>
                <div className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-subtle)" }}>
                  <img src={sharedCover} alt="Project cover" className="block w-full" />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={onRegenerateCover} disabled={coverBusy} data-testid="project-regen-cover">
                    <RefreshCcw className={cn("h-3.5 w-3.5", coverBusy && "animate-spin")} /> Regenerate
                  </Button>
                  <Button size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" onClick={onUploadCover} disabled={coverBusy} data-testid="project-upload-cover">
                    <ImageIcon className="h-3.5 w-3.5" /> Upload
                  </Button>
                </div>
              </div>
            )}
            {supporting.map((asset) => (
              <div key={asset.id} className={cn("rounded-xl border bg-[color:var(--surface-1)] p-4", asset.asset_type === "research_brief" && "lg:col-span-2")} style={{ borderColor: "var(--border-subtle)" }}>
                <div className="mb-2 flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-[color:var(--accent-cyan)]" />
                  <h4 className="text-[13px] font-semibold text-1">{ASSET_TYPE_LABELS[asset.asset_type] || asset.asset_type}</h4>
                </div>
                <p className="whitespace-pre-wrap text-[12.5px] leading-[1.6] text-foreground/85">{asset.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function NextActionPanel({ hub }) {
  const action = hub.nextAction;
  const toneClass = {
    draft: "border-[color:var(--accent-blue)]/40 bg-[color:var(--accent-blue)]/12 text-[color:var(--accent-cyan)]",
    review: "border-amber-200 bg-[color:var(--accent-amber)]/12 text-[color:var(--accent-amber)]",
    approved: "border-emerald-200 bg-[color:var(--accent-green)]/12 text-[color:var(--accent-green)]",
    scheduled: "border-[color:var(--accent-blue)]/40 bg-[color:var(--accent-blue)]/12 text-[color:var(--accent-cyan)]",
    published: "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-muted-foreground",
  }[action.tone] || "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-muted-foreground";

  return (
    <section className="rounded-xl border bg-card p-4 inner-highlight" style={{ borderColor: "var(--border-subtle)" }} data-testid="project-next-action">
      <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Next move</p>
      <span className={cn("mt-2 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold", toneClass)}>{action.label}</span>
      <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{action.hint}</p>
      <Button asChild className="mt-4 w-full gap-1.5" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
        <Link to="/create"><Sparkles className="h-4 w-4" /> Create from this project</Link>
      </Button>
    </section>
  );
}

function CalendarPanel({ items }) {
  return (
    <section className="rounded-xl border bg-card p-4 inner-highlight" style={{ borderColor: "var(--border-subtle)" }} data-testid="project-calendar-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Calendar</p>
        <Button asChild variant="ghost" size="sm" className="h-7 gap-1 text-[11px] text-muted-foreground">
          <Link to="/calendar"><ExternalLink className="h-3 w-3" /> Open</Link>
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="mt-3 rounded-lg border border-dashed p-3 text-[12.5px] text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
          No scheduled items tied to this project yet.
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          {items.slice(0, 4).map((item) => (
            <div key={item.id} className="rounded-lg border bg-[color:var(--surface-1)] p-3" style={{ borderColor: "var(--border-subtle)" }}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="line-clamp-1 text-[13px] font-semibold text-1">{item.title}</p>
                  <p className="mt-1 text-[11.5px] text-muted-foreground">{formatDateTime(item.scheduled_at)}</p>
                </div>
                <StatusBadge status={item.status} />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function SourceMaterialPanel({ items }) {
  return (
    <section className="rounded-xl border bg-card p-4 inner-highlight" style={{ borderColor: "var(--border-subtle)" }} data-testid="project-source-panel">
      <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Related source material</p>
      {items.length === 0 ? (
        <p className="mt-3 rounded-lg border border-dashed p-3 text-[12.5px] text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
          No related inbox items found in the current workspace.
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          {items.slice(0, 3).map((item) => (
            <div key={item.id} className="rounded-lg border bg-[color:var(--surface-1)] p-3" style={{ borderColor: "var(--border-subtle)" }}>
              <p className="line-clamp-1 text-[13px] font-semibold text-1">{item.title}</p>
              <p className="mt-1 line-clamp-2 text-[11.5px] leading-relaxed text-muted-foreground">{item.preview}</p>
              <p className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground">{item.source} · {timeAgo(item.received_at)}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function RunsPanel({ runs }) {
  return (
    <section className="rounded-xl border bg-card p-4 inner-highlight" style={{ borderColor: "var(--border-subtle)" }} data-testid="project-runs-panel">
      <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Agent runs</p>
      {runs.length === 0 ? (
        <p className="mt-3 rounded-lg border border-dashed p-3 text-[12.5px] text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
          No agent runs are attached to this project yet.
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          {runs.slice(0, 4).map((run) => (
            <Link key={run.id} to={`/runs/${run.id}`} className="block rounded-lg border bg-[color:var(--surface-1)] p-3 hover:bg-card" style={{ borderColor: "var(--border-subtle)" }}>
              <div className="flex items-start justify-between gap-2">
                <p className="line-clamp-1 text-[13px] font-semibold text-1">{run.name || run.title || run.id}</p>
                <StatusBadge status={run.status === "completed" ? "approved" : run.status} />
              </div>
              <p className="mt-1 text-[11.5px] text-muted-foreground">Updated {timeAgo(run.finished_at || run.started_at || run.created_at)}</p>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function HubMetric({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-xl border bg-card p-4 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
      <div className="flex items-start justify-between gap-2">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[color:var(--accent-cyan)]/40 bg-[color:var(--accent-green)]/12 text-[color:var(--accent-green)]">
          <Icon className="h-4 w-4" />
        </span>
        <span className="font-mono text-2xl font-semibold tracking-[-0.04em] text-foreground">{value}</span>
      </div>
      <p className="mt-3 text-sm font-semibold text-1">{label}</p>
      <p className="mt-1 text-[12px] text-muted-foreground">{hint}</p>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-md border bg-card p-3" style={{ borderColor: "var(--border-subtle)" }}>
      <div className="flex items-center justify-between gap-2">
        <Icon className="h-3.5 w-3.5 text-[color:var(--accent-cyan)]" />
        <span className="max-w-[92px] truncate text-sm font-bold tracking-[-0.03em] text-foreground">{value}</span>
      </div>
      <p className="mt-2 text-[11.5px] font-medium text-muted-foreground">{label}</p>
    </div>
  );
}
